import { useRef as useLocalityRef } from 'react';
import { LocalityFilter } from 'rentalui-finance';
import { useLocation as useRecoveryLocation } from 'react-router-dom';
import { client as recoveryClient } from '../api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Form, Nav, Spinner, Tab } from 'react-bootstrap';
import ResponsiveTable from '../components/ResponsiveTable';
import SidePanelFilters from '../components/SidePanelFilters';
import ColumnToggle, { useHiddenColumns } from '../components/ColumnToggle';
import DeliveryPoolPricingModal, { poolPricingSummary } from '../components/DeliveryPoolPricingModal';
import {
  adminCreateUser, adminListProperties, adminListUsers, adminListVisits, adminUpdateProperty,
  adminSetInvoicingPermission, uploadImages
} from '../api';
import { useAuth } from '../auth';
import AddressAutocomplete from '../components/AddressAutocomplete';
import DashboardTabs from '../components/DashboardTabs';
import PlansAdminPanel from '../components/PlansAdminPanel';
import SupportTicketsPanel from '../components/SupportTicketsPanel';
import EmailsPanel from '../components/EmailsPanel';
import FinanceApp from 'rentalui-finance';
import { API_BASE_URL } from '../api';
import BusinessAnalytics from '../components/BusinessAnalytics';
import OnboardingPanel from '../components/OnboardingPanel';
import PlatformSettingsPanel from '../components/PlatformSettingsPanel';
import CompanyProfilePanel from '../components/CompanyProfilePanel';
import AgentRequestsPanel from '../components/AgentRequestsPanel';
import PropertyDetailsModal from '../components/PropertyDetailsModal';
import HomepageRankModal from '../components/HomepageRankModal';
import { VisitDetailsModal, usePagedArray, Pager } from 'rentalui-finance';
import { VisitStatus, VisitActions } from '../components/VisitActions';
import { VISIT_RANGE_OPTIONS, visitInRange } from '../components/visitRanges';
import InvoiceStatsPanel from '../components/InvoiceStatsPanel';

// 05_admin_agent_registration.md - the website owner registers a site-visit agent (`delivery`
// role) directly here, same as the jewelry admin Users page. `delivery` also needs Aadhaar KYC +
// an address (adminCreateUserSchema enforces it), collected below when role === 'delivery'. The
// form also creates the 3 finance_* roles + admin, which aren't self-serve either.
const ROLE_LABEL = {
  customer: 'Customer', owner: 'Rental owner', admin: 'Website owner', delivery: 'Site-visit agent',
  finance_employee: 'Finance (view)', finance_lead: 'Finance lead', finance_manager: 'Finance manager'
};

const emptyStaffUser = {
  name: '', email: '', password: '', phone: '', role: 'delivery', serviceArea: '',
  aadhaarNumber: '', aadhaarCardUrl: '', selfieUrl: '',
  drivingLicenceNumber: '', drivingLicenceUrl: '',
  line1: '', line2: '', area: '', city: '', state: '', pincode: '',
  lat: null, lng: null, placeId: '', formatted: ''
};

// Address fields lifted in/out of the flat newStaffUser state for AddressAutocomplete.
const STAFF_ADDRESS_KEYS = ['line1', 'line2', 'area', 'city', 'state', 'pincode', 'lat', 'lng', 'placeId', 'formatted'];

// admin_real_estate_property_management.md - a listing is taken off the public site by disabling
// it, never hard-deleted; ITEM_STATUSES in rentalapi's common/itemStatus.js.
const PROPERTY_STATUSES = ['available', 'booked', 'maintenance', 'disabled'];
const PROPERTY_STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'all', label: 'All' }
];

// Settled cancellations get their own tab - a cancelled visit with nothing left to action (no
// pending reschedule) is kept out of the active "Visit Requests" list.
function isSettledCancelledVisit(visit) {
  if (visit.status !== 'cancelled') return false;
  const reschedulePending = visit.extension?.requested && visit.extension.status === 'pending';
  return !reschedulePending;
}

const userRoles = (u) => (u.effectiveRoles && u.effectiveRoles.length
  ? u.effectiveRoles
  : [...new Set([u.role, ...(u.roles || [])].filter(Boolean))]);

// Optional columns the admin can hide on the Properties list (ColumnToggle.js).
const PROPERTY_OPT_COLS = [
  { key: 'owner', label: 'Owner' },
  { key: 'location', label: 'Location' }
];

// 08_owner_invoice_pdf_export.md - hidden from every owner unless the admin explicitly grants it
// here; only meaningful for the 'owner' role (see the Users table below).
function InvoicingPermissionCell({ user, onSaved }) {
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      await adminSetInvoicingPermission(user._id, !user.allowGstExemptInvoices);
      onSaved();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Form.Check
      type="switch"
      id={`gst-exempt-${user._id}`}
      checked={!!user.allowGstExemptInvoices}
      disabled={saving}
      onChange={toggle}
      label={<span className="small text-muted">No-GST invoices</span>}
    />
  );
}

export default function AdminDashboard() {
  const [adminLocality, setAdminLocality] = useState('');
  const [adminLocalities, setAdminLocalities] = useState([]);
  const adminLocalityRef = useLocalityRef('');
  const recoveryLocation = useRecoveryLocation();
  useEffect(() => {
    const params = new URLSearchParams(recoveryLocation.search);
    const id = params.get('recoveryCase');
    const bookingId = params.get('bookingId');
    if (!id && !bookingId) return undefined;
    let alive = true;
    recoveryClient.get(bookingId ? `/api/order-attention/orders/real_estate/${encodeURIComponent(bookingId)}` : `/api/order-attention/${id}/details`).then(response => {
      if (bookingId) response = { data: { data: { order: response.data.data } } };
      if (alive && response.data.data.order) setDetailsVisit(response.data.data.order);
    }).catch(() => { if (alive) setError('Unable to open the requested order. Return to its recovery page and retry.'); });
    return () => { alive = false; };
  }, [recoveryLocation.search]);

  const { user, token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newStaffUser, setNewStaffUser] = useState(emptyStaffUser);
  const [userMsg, setUserMsg] = useState('');
  const [userErr, setUserErr] = useState('');
  const [detailsVisit, setDetailsVisit] = useState(null); // fix_issues.md follow-up
  const [propertyStatusFilter, setPropertyStatusFilter] = useState('active'); // admin_real_estate_property_management.md
  const [detailsPropertyId, setDetailsPropertyId] = useState(null); // property-details popup
  const [todayRange, setTodayRange] = useState('today'); // "Today's Visits" tab range filter
  const [usersTab, setUsersTab] = useState('accounts'); // accounts | addstaff | requests
  const [poolUser, setPoolUser] = useState(null); // "Manage pay" modal
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('active');
  const [userSearch, setUserSearch] = useState('');
  const propCols = useHiddenColumns('rv:real_estate:adminPropertyCols', []);
  // v3_fix_issues #14 - workflow tabs first, analytics/settings behind a "More" menu.
  const [activeTab, setActiveTab] = useState(() => (user?.role === 'admin' ? 'today' : 'finance'));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    // The properties/visits/users tabs are admin-only - a finance_* role can reach this page for
    // the Finance tab below (which gates itself per-endpoint) but would 403 on these, so skip
    // them entirely rather than let one 403 block the whole page from loading.
    if (user?.role !== 'admin') {
      setLoading(false);
      return;
    }
    try {
      const [allProperties, allVisits, allUsers] = await Promise.all([
        adminListProperties(propertyStatusFilter),
        adminListVisits(),
        adminListUsers()
      ]);
      setProperties(allProperties);
      setVisits(allVisits);
      setUsers(allUsers);
    } catch (err) {
      setError('Could not load the admin dashboard.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const loadProperties = async (status) => {
    try {
      setProperties(await adminListProperties(status));
    } catch {
      setError('Could not load properties.');
    }
  };

  const handlePropertyStatusFilter = (e) => {
    const next = e.target.value;
    setPropertyStatusFilter(next);
    loadProperties(next);
  };

  const handleChangePropertyStatus = async (property, nextStatus) => {
    if (nextStatus === property.status) return;
    try {
      await adminUpdateProperty(property._id, { status: nextStatus });
      loadProperties(propertyStatusFilter);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to update that property.');
    }
  };

  // homepage_refactor.md - the admin-only "Verified" badge shown on the homepage Trending strip.
  const handleToggleVerified = async (property) => {
    try {
      await adminUpdateProperty(property._id, { verified: !property.verified });
      loadProperties(propertyStatusFilter);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to update that property.');
    }
  };

  // admin_homepage_ranking requirement - website-admin-only "premium placement" (1-100, shared
  // platform-wide) that leads the homepage's cross-vertical featured rail.
  const [rankModalItem, setRankModalItem] = useState(null);
  const handleSaveRank = async (rank) => {
    await adminUpdateProperty(rankModalItem._id, { homepageRank: rank });
    loadProperties(propertyStatusFilter);
  };

  const handleStaffFileUpload = (key, label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUserErr('');
    try {
      const [url] = await uploadImages([file]);
      setNewStaffUser((p) => ({ ...p, [key]: url }));
    } catch {
      setUserErr(`Could not upload the ${label} image (JPEG/PNG).`);
    }
  };

  const handleCreateStaffUser = async (e) => {
    e.preventDefault();
    setUserMsg('');
    setUserErr('');
    const s = newStaffUser;
    const payload = { name: s.name, email: s.email, password: s.password, phone: s.phone, role: s.role };
    if (s.role === 'delivery') {
      payload.serviceArea = s.serviceArea;
      payload.kyc = { aadhaarNumber: s.aadhaarNumber, aadhaarCardUrl: s.aadhaarCardUrl, selfieUrl: s.selfieUrl, drivingLicenceNumber: s.drivingLicenceNumber, drivingLicenceUrl: s.drivingLicenceUrl };
      payload.address = {
        line1: s.line1, line2: s.line2, area: s.area, city: s.city, state: s.state, pincode: s.pincode,
        lat: s.lat ?? null, lng: s.lng ?? null, placeId: s.placeId || '', formatted: s.formatted || ''
      };
    }
    try {
      await adminCreateUser(payload);
      setUserMsg(s.role === 'delivery' ? 'Agent account created.' : `${s.role} account created.`);
      setNewStaffUser(emptyStaffUser);
      load();
    } catch (err) {
      setUserErr(err.response?.data?.msg || 'Unable to create the account.');
    }
  };

  const isAdmin = user?.role === 'admin';
  const isFinanceRole = ['finance_employee', 'finance_lead', 'finance_manager'].includes(user?.role);

  const activeVisits = visits.filter((v) => !isSettledCancelledVisit(v));
  const cancelledVisits = visits.filter(isSettledCancelledVisit);
  const todaysVisits = visits.filter((v) => visitInRange(v, todayRange));

  const matchesAdminLocality = row => !adminLocality || String(row.itemLocation || row.location || properties.find(property => String(property._id) === String(row.itemId))?.location || '').toLowerCase().includes(adminLocality.toLowerCase());
  const todaysVisitsPaged = usePagedArray(todaysVisits.filter(matchesAdminLocality), { resetKey: `${activeTab}:${todayRange}:${adminLocality}` });
  const activeVisitsPaged = usePagedArray(activeVisits.filter(matchesAdminLocality), { resetKey: `${activeTab}:${adminLocality}` });
  const cancelledVisitsPaged = usePagedArray(cancelledVisits.filter(matchesAdminLocality), { resetKey: `${activeTab}:${adminLocality}` });
  const propertiesPaged = usePagedArray(properties.filter(matchesAdminLocality), { resetKey: `${activeTab}:${propertyStatusFilter}:${adminLocality}` });
  // Client-side filtering for the Accounts sub-tab (real_estate's user list isn't server-paginated).
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users.filter((u) => {
      if (userStatusFilter === 'active' && u.status === 'disabled') return false;
      if (userStatusFilter === 'disabled' && u.status !== 'disabled') return false;
      if (userRoleFilter && !userRoles(u).includes(userRoleFilter)) return false;
      if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, userStatusFilter, userRoleFilter, userSearch]);
  const usersPaged = usePagedArray(filteredUsers, { resetKey: `${activeTab}:${userStatusFilter}:${userRoleFilter}:${userSearch}` });

  if (loading) return <Spinner animation="border" role="status" />;

  const renderVisitsTable = (paged, emptyText) => (
    <>
    <ResponsiveTable
      cardsBelow="xl"
      rows={paged.rows}
      rowKey={(v) => v._id}
      empty={emptyText}
      columns={[
        {
          key: 'visit', header: 'Visit', card: 'title',
          /* fix_issues.md follow-up - opens the full details (property address + owner details
             together), same "Details" popup pattern used elsewhere. */
          cell: (visit) => (
            <Button size="sm" variant="link" className="p-0 align-baseline" onClick={() => setDetailsVisit(visit)}>
              {visit._id.slice(-6).toUpperCase()}
            </Button>
          )
        },
        {
          key: 'requestedBy', header: 'Requested by', card: 'subtitle',
          cell: (visit) => (<>{visit.customerName}<br /><small className="text-muted rv-email" title={visit.email}>{visit.email}</small></>)
        },
        // fix_issues.md follow-up - the site owner sees the property's own address + owner details.
        {
          key: 'property', header: 'Property', hideBelow: 'md',
          cell: (visit) => (<>{visit.itemName}{visit.itemLocation && <><br /><small className="text-muted">{visit.itemLocation}</small></>}</>)
        },
        { key: 'owner', header: 'Owner', hideBelow: 'md', cell: (visit) => visit.ownerName || <span className="text-muted">—</span> },
        { key: 'date', header: 'Date', cell: (visit) => new Date(visit.startDate).toLocaleDateString() },
        { key: 'notes', header: 'Notes', hideBelow: 'md', cell: (visit) => visit.notes },
        { key: 'status', header: 'Status', card: 'badge', cell: (visit) => <VisitStatus visit={visit} /> }
      ]}
      /* The website owner can drive a visit forward with the same controls the rental owner has -
         every visit endpoint already accepts an admin caller. */
      actions={(visit) => <VisitActions visit={visit} areaHint={visit.itemLocation || ''} onChanged={load} />}
    />
    <Pager {...paged} />
    </>
  );

  const primaryNav = isAdmin
    ? [['today', `Today's Visits (${todaysVisits.length})`], ['visits', `Visit Requests (${activeVisits.length})`], ['properties', `Properties (${properties.length})`]]
    : (isFinanceRole ? [['finance', 'Finance']] : []);
  const moreNav = isAdmin
    ? [['visits-cancelled', `Cancelled (${cancelledVisits.length})`], ['business', 'Business'], ['users', `Users (${users.length})`], ['finance', 'Finance'], ['invoiceUsage', 'Invoice usage'], ['onboarding', 'Onboarding'], ['supportTickets', 'Support tickets'], ['emails', 'Email log'], ['settings', 'Settings'], ['companyProfile', 'Company profile'], ['plans', 'Subscription']]
    : (isFinanceRole ? [['business', 'Business'], ['plans', 'Subscription reviews']] : []);

  return (
    <div>
      <h3 className="mb-3">Admin dashboard</h3>
      {isAdmin && ['items', 'properties', 'bookings', 'today', 'visits', 'cancelled'].includes(activeTab) && <LocalityFilter value={adminLocality} options={properties.map(property => property.location).filter(Boolean)} onChange={setAdminLocality} />}
      {error && <Alert variant="danger">{error}</Alert>}

      <DashboardTabs activeKey={activeTab} onSelect={setActiveTab} primary={primaryNav} more={moreNav}>
        <Tab.Pane eventKey="properties">
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <span className="small text-muted">Show</span>
            <Form.Select size="sm" style={{ maxWidth: 160 }} value={propertyStatusFilter} onChange={handlePropertyStatusFilter}>
              {PROPERTY_STATUS_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </Form.Select>
            <span className="small text-muted">Listings are disabled, never deleted.</span>
            <span className="ms-auto d-flex gap-2">
              <ColumnToggle options={PROPERTY_OPT_COLS} hidden={propCols.hidden} onToggle={propCols.toggle} label="Property columns" />
              <Button size="sm" as={Link} to="/admin/properties/new">+ Add a property</Button>
            </span>
          </div>
          <ResponsiveTable
            rows={propertiesPaged.rows}
            rowKey={(p) => p._id}
            empty="No properties here."
            rowClassName={(p) => (p.status === 'disabled' ? 'text-muted' : '')}
            columns={[
              {
                key: 'name', header: 'Name', card: 'title',
                cell: (property) => (<>{property.name}{property.category && <div className="small text-muted">{property.category}</div>}</>)
              },
              {
                key: 'owner', header: 'Owner', hideBelow: 'md', card: 'subtitle',
                cell: (property) => (property.ownerName
                  ? <>{property.ownerName}{property.ownerEmail && <div className="small text-muted">{property.ownerEmail}</div>}</>
                  : <span className="text-muted">—</span>)
              },
              { key: 'location', header: 'Location', hideBelow: 'md', cell: (property) => property.location },
              { key: 'price', header: 'Price', className: 'text-nowrap', cell: (property) => `₹${property.price}/${property.priceUnit}` },
              {
                key: 'status', header: 'Status',
                cell: (property) => (
                  <div style={{ minWidth: 140 }}>
                    <Form.Select
                      size="sm"
                      value={property.status}
                      onChange={(e) => handleChangePropertyStatus(property, e.target.value)}
                      aria-label={`Status for ${property.name}`}
                    >
                      {PROPERTY_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </Form.Select>
                    {property.verified && <Badge bg="success" className="mt-1">✓ Verified</Badge>}
                    {property.homepageRank != null && <Badge bg="warning" text="dark" className="mt-1">★ Rank #{property.homepageRank}</Badge>}
                  </div>
                )
              }
            ].filter((c) => !propCols.hidden.has(c.key))}
            actions={(property) => [
              { label: 'Details', variant: 'link', onClick: () => setDetailsPropertyId(property._id) },
              { label: 'Edit', href: `/admin/properties/${property._id}/edit` },
              {
                label: property.verified ? 'Unverify' : 'Verify',
                variant: property.verified ? 'outline-secondary' : 'success',
                onClick: () => handleToggleVerified(property)
              },
              // admin_homepage_ranking requirement - "premium placement" on the homepage.
              {
                label: property.homepageRank != null ? `Rank #${property.homepageRank}` : 'Set homepage rank',
                variant: property.homepageRank != null ? 'outline-warning' : 'outline-primary',
                onClick: () => setRankModalItem(property)
              }
            ]}
          />
          <Pager {...propertiesPaged} />
        </Tab.Pane>

        {/* Today's schedule - same "what's happening now" focus as jewelry's delivery view. */}
        <Tab.Pane eventKey="today">
          <Form.Select
            size="sm" style={{ maxWidth: 180 }} className="mb-3"
            value={todayRange} onChange={(e) => setTodayRange(e.target.value)}
            aria-label="Visit date range"
          >
            {VISIT_RANGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </Form.Select>
          {renderVisitsTable(todaysVisitsPaged, 'No visits in this range.')}
        </Tab.Pane>

        <Tab.Pane eventKey="visits">
          {renderVisitsTable(activeVisitsPaged, 'No visit requests.')}
        </Tab.Pane>

        {/* Settled cancellations get their own tab - cancelled visits with no pending action. */}
        <Tab.Pane eventKey="visits-cancelled">
          {renderVisitsTable(cancelledVisitsPaged, 'No cancelled visits.')}
        </Tab.Pane>

        <Tab.Pane eventKey="users">
          <Nav variant="pills" className="mb-3 gap-1 flex-wrap" activeKey={usersTab} onSelect={(k) => k && setUsersTab(k)}>
            <Nav.Item><Nav.Link eventKey="accounts">Accounts ({users.length})</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="addstaff">Add staff account</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="requests">Agent requests</Nav.Link></Nav.Item>
          </Nav>

          {usersTab === 'accounts' && (
            <div className="d-flex flex-column flex-md-row-reverse gap-3">
              <SidePanelFilters
                badge={(userRoleFilter ? 1 : 0) + (userSearch.trim() ? 1 : 0) + (userStatusFilter !== 'active' ? 1 : 0)}
                onClear={() => { setUserStatusFilter('active'); setUserRoleFilter(''); setUserSearch(''); }}
              >
                <Form.Label className="small mb-1">Account status</Form.Label>
                <Form.Select size="sm" className="mb-2" value={userStatusFilter} onChange={(e) => setUserStatusFilter(e.target.value)}>
                  <option value="active">Active</option>
                  <option value="disabled">Inactive / disabled</option>
                  <option value="all">All</option>
                </Form.Select>
                <Form.Label className="small mb-1">Role</Form.Label>
                <Form.Select size="sm" className="mb-2" value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)}>
                  <option value="">Any role</option>
                  {['customer', 'owner', 'admin', 'delivery', 'finance_employee', 'finance_lead', 'finance_manager'].map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </Form.Select>
                <Form.Label className="small mb-1">Search</Form.Label>
                <Form.Control size="sm" placeholder="Name or email" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
              </SidePanelFilters>

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <ResponsiveTable
                  rows={usersPaged.rows}
                  rowKey={(u) => u._id}
                  empty="No users match."
                  columns={[
                    { key: 'name', header: 'Name', card: 'title', cell: (u) => u.name },
                    { key: 'email', header: 'Email', card: 'subtitle', cell: (u) => <span className="rv-email" title={u.email}>{u.email}</span> },
                    {
                      key: 'role', header: 'Role',
                      cell: (u) => (
                        <div className="d-flex flex-wrap gap-1">
                          {userRoles(u).map((r) => <Badge key={r} bg="secondary">{r}</Badge>)}
                        </div>
                      )
                    },
                    { key: 'phone', header: 'Phone', hideBelow: 'md', cell: (u) => u.phone },
                    {
                      key: 'pool', header: 'Agent pool & pay', hideBelow: 'md', hideEmpty: true,
                      cell: (u) => {
                        if (!userRoles(u).includes('delivery')) return null;
                        const scope = u.onboardedByOwnerId ? 'Private to an owner' : 'Platform agent';
                        const fee = poolPricingSummary(u, 'agent', { pool: false }) || 'Fee: platform default';
                        return <span className="small">{scope} · {fee}</span>;
                      }
                    },
                    {
                      key: 'invoicing', header: 'Invoicing', hideEmpty: true,
                      /* 08_owner_invoice_pdf_export.md - lets THIS owner bill without GST; hidden for
                         every other role. */
                      cell: (u) => (userRoles(u).includes('owner') ? <InvoicingPermissionCell user={u} onSaved={load} /> : null)
                    }
                  ]}
                  actions={(u) => (userRoles(u).includes('delivery')
                    ? [{ label: 'Manage pay', variant: 'link', onClick: () => setPoolUser(u) }]
                    : null)}
                />
                <Pager {...usersPaged} />
              </div>
            </div>
          )}

          {usersTab === 'requests' && (
            <div className="mt-1"><AgentRequestsPanel /></div>
          )}

          {usersTab === 'addstaff' && (
            <div style={{ maxWidth: 680 }}>
                  <p className="text-muted small">Register a site-visit agent here, or via the <strong>Agent requests</strong> tab. Finance and admin accounts aren&apos;t self-serve via signup - create them here too. An agent account also needs Aadhaar KYC + an address.</p>
                  {userMsg && <Alert variant="success" className="py-2" onClose={() => setUserMsg('')} dismissible>{userMsg}</Alert>}
                  {userErr && <Alert variant="danger" className="py-2" onClose={() => setUserErr('')} dismissible>{userErr}</Alert>}
                  <Form onSubmit={handleCreateStaffUser}>
                    <Form.Group className="mb-2" controlId="staff-role">
                <Form.Label className="visually-hidden">Role</Form.Label>
                <Form.Select size="sm" value={newStaffUser.role} onChange={(e) => setNewStaffUser((p) => ({ ...p, role: e.target.value }))}>
                  <option value="delivery">Site-visit agent</option>
                  <option value="finance_employee">Finance employee (view only)</option>
                  <option value="finance_lead">Finance lead (approve up to threshold)</option>
                  <option value="finance_manager">Finance manager (approve any + edit config)</option>
                  <option value="admin">Website owner (admin)</option>
                </Form.Select>
              </Form.Group>
              <div className="row g-2">
                <div className="col-md-6"><Form.Control size="sm" placeholder="Name" required value={newStaffUser.name} onChange={(e) => setNewStaffUser((p) => ({ ...p, name: e.target.value }))} /></div>
                <div className="col-md-6"><Form.Control size="sm" type="email" placeholder="Email" required value={newStaffUser.email} onChange={(e) => setNewStaffUser((p) => ({ ...p, email: e.target.value }))} /></div>
                <div className="col-md-6"><Form.Control size="sm" type="password" placeholder="Temporary password" required minLength={8} value={newStaffUser.password} onChange={(e) => setNewStaffUser((p) => ({ ...p, password: e.target.value }))} /></div>
                <div className="col-md-6"><Form.Control size="sm" placeholder="Phone (optional)" value={newStaffUser.phone} onChange={(e) => setNewStaffUser((p) => ({ ...p, phone: e.target.value }))} /></div>
              </div>
              {newStaffUser.role === 'delivery' && (
                <>
                  <Form.Control size="sm" className="mt-2" placeholder="Service area (e.g. Bandra, Mumbai)" value={newStaffUser.serviceArea} onChange={(e) => setNewStaffUser((p) => ({ ...p, serviceArea: e.target.value }))} />
                  <div className="row g-2 mt-1">
                    <div className="col-md-6"><Form.Control size="sm" placeholder="Aadhaar number (12 digits)" required inputMode="numeric" maxLength={12} value={newStaffUser.aadhaarNumber} onChange={(e) => setNewStaffUser((p) => ({ ...p, aadhaarNumber: e.target.value }))} /></div>
                    <div className="col-md-6"><Form.Label className="small mb-0">Aadhaar card</Form.Label><Form.Control size="sm" type="file" accept="image/*" onChange={handleStaffFileUpload('aadhaarCardUrl', 'Aadhaar')} />{newStaffUser.aadhaarCardUrl && <span className="small text-success">Uploaded ✓</span>}</div>
                  </div>
                  {/* staff_selfie_kyc.md - mandatory camera selfie (`capture="user"` = front camera on mobile). */}
                  <div className="row g-2 mt-1">
                    <div className="col-md-6"><Form.Label className="small mb-0">Selfie (camera)</Form.Label><Form.Control size="sm" type="file" accept="image/*" capture="user" onChange={handleStaffFileUpload('selfieUrl', 'selfie')} />{newStaffUser.selfieUrl && <span className="small text-success">Uploaded ✓</span>}</div>
                  </div>
                  <div className="row g-2 mt-1">
                    <div className="col-md-6"><Form.Control size="sm" placeholder="Driving-licence number (optional)" value={newStaffUser.drivingLicenceNumber} onChange={(e) => setNewStaffUser((p) => ({ ...p, drivingLicenceNumber: e.target.value }))} /></div>
                    <div className="col-md-6"><Form.Label className="small mb-0">Driving licence</Form.Label><Form.Control size="sm" type="file" accept="image/*" onChange={handleStaffFileUpload('drivingLicenceUrl', 'driving-licence')} />{newStaffUser.drivingLicenceUrl && <span className="small text-success">Uploaded ✓</span>}</div>
                  </div>
                  <AddressAutocomplete
                    size="sm"
                    showState
                    required
                    idPrefix="staff-addr"
                    value={STAFF_ADDRESS_KEYS.reduce((o, k) => ({ ...o, [k]: newStaffUser[k] }), {})}
                    onChange={(a) => setNewStaffUser((p) => ({ ...p, ...a }))}
                  />
                </>
              )}
              <Button size="sm" type="submit" className="mt-2">Create account</Button>
            </Form>
            </div>
          )}
        </Tab.Pane>

        {/* v3_fix_issues #7 & #8 - the website owner's platform-performance overview. Its own
            tab, alongside Finance's operational queues. Platform-wide (covers every vertical's
            bookings, not just real estate). */}
        <Tab.Pane eventKey="business">
          <BusinessAnalytics />
        </Tab.Pane>

        <Tab.Pane eventKey="finance">
          <FinanceApp
            embedded
            apiBaseUrl={API_BASE_URL}
            getToken={() => token}
            vertical="real_estate"
            actorRole={user?.role}
            renderVisitActions={(visit, close) => (
              <VisitActions
                visit={visit}
                areaHint={visit.itemLocation || ''}
                onChanged={() => { load(); close(); }}
              />
            )}
            onOpenExternal={(kind, entity) => {
              if (kind === 'booking') {
                setActiveTab('visits');
                setDetailsVisit(entity);
              }
              if (kind === 'listing') {
                setActiveTab('properties');
                if (entity?._id) setDetailsPropertyId(entity._id);
              }
              if (kind === 'staff') setActiveTab('users');
            }}
          />
        </Tab.Pane>

        <Tab.Pane eventKey="invoiceUsage" mountOnEnter unmountOnExit>
          <InvoiceStatsPanel />
        </Tab.Pane>

        {/* leads_business_flow_requirement.md - one page, two tabs: the outbound leads pipeline
            and the inbound "Request a Demo" queue (v3_fix_issues #10) - every demo request naming
            this vertical also auto-appears as a lead. */}
        <Tab.Pane eventKey="onboarding" mountOnEnter unmountOnExit>
          <OnboardingPanel />
        </Tab.Pane>
        <Tab.Pane eventKey="supportTickets" mountOnEnter unmountOnExit>
          <SupportTicketsPanel />
        </Tab.Pane>
        <Tab.Pane eventKey="emails" mountOnEnter unmountOnExit>
          <EmailsPanel scope="admin" />
        </Tab.Pane>
        <Tab.Pane eventKey="settings" mountOnEnter unmountOnExit>
          <PlatformSettingsPanel />
        </Tab.Pane>
        <Tab.Pane eventKey="companyProfile" mountOnEnter unmountOnExit>
          <CompanyProfilePanel />
        </Tab.Pane>
        <Tab.Pane eventKey="plans" mountOnEnter unmountOnExit>
          <PlansAdminPanel />
        </Tab.Pane>
      </DashboardTabs>

      {/* v6 finance_module — the single shared visit popup (Part C): full detail + VisitStatus +
          the same VisitActions the rental owner gets (every backing route already authorises
          admin), replacing the old read-only status-badge-only modal. */}
      <VisitDetailsModal
        visit={detailsVisit}
        show={!!detailsVisit}
        onHide={() => setDetailsVisit(null)}
        renderActions={(visit) => (
          <VisitActions
            visit={visit}
            areaHint={visit.itemLocation || ''}
            onChanged={() => { load(); setDetailsVisit(null); }}
          />
        )}
      />

      <PropertyDetailsModal
        propertyId={detailsPropertyId}
        show={!!detailsPropertyId}
        onHide={() => setDetailsPropertyId(null)}
      />
      <HomepageRankModal
        item={rankModalItem}
        show={!!rankModalItem}
        onHide={() => setRankModalItem(null)}
        onSave={handleSaveRank}
      />
      <DeliveryPoolPricingModal
        user={poolUser}
        show={!!poolUser}
        poolKind="agent"
        showPool={false}
        showB2B={false}
        onHide={() => setPoolUser(null)}
        onChanged={load}
      />
    </div>
  );
}
